import { useEffect, useState } from 'react'
import { getContact } from '../api'
import { getOrganisation } from '../organisations/api'
import type { CaptureContext } from './types'

/** The name of a contact; a merged one answers with the name of the contact it went to. */
export async function contactName(id: string): Promise<string | null> {
  const found = await getContact(id)
  if (!('moved_to' in found)) return found.full_name
  const target = await getContact(found.moved_to)
  return 'moved_to' in target ? null : target.full_name
}

/** The name of the relation a memo is about, or null while it loads (or when it cannot be read). */
export function useRelationName(context: CaptureContext | null): string | null {
  const [found, setFound] = useState<{ key: string; name: string | null } | null>(null)
  const key = context ? `${context.type}:${context.id}` : null
  useEffect(() => {
    if (!context || !key) return
    let alive = true
    const load =
      context.type === 'contact'
        ? contactName(context.id)
        : getOrganisation(context.id).then((organisation) => ('name' in organisation ? organisation.name : null))
    load.then((name) => alive && setFound({ key, name })).catch(() => alive && setFound({ key, name: null }))
    return () => {
      alive = false
    }
  }, [context, key])
  return found && found.key === key ? found.name : null
}
